export type VariantStock = {
  quantity: number;
  isExact: boolean;
};

export type ProductStock = Record<number, VariantStock>;

export type ProductStockData = {
  variantStock: ProductStock;
  unavailableLines: string[];
};

type ProductStockPayload = {
  catalogue?: { variants?: unknown; truncatedCollections?: unknown };
  cartAvailability?: { unavailableLines?: unknown; isComplete?: unknown };
};

const PRODUCT_STOCK_SECTION = 'product-stock';
const PRODUCT_STOCK_SELECTOR = '[data-product-stock]';
const PRODUCT_STOCK_REQUEST_TIMEOUT = 10000;

const isVariantStockRecord = (
  record: unknown,
): record is { id: number; quantity: number; isExact: boolean } => {
  if (!record || typeof record !== 'object') return false;
  const { id, quantity, isExact } = record as Record<string, unknown>;
  return (
    Number.isInteger(id) &&
    (id as number) > 0 &&
    Number.isInteger(quantity) &&
    typeof isExact === 'boolean'
  );
};

const normaliseVariants = (variants: unknown): ProductStock => {
  if (!Array.isArray(variants)) return {};
  return variants
    .filter(isVariantStockRecord)
    .reduce<ProductStock>((stock, { id, quantity, isExact }) => {
      stock[id] = { quantity: Math.max(0, quantity), isExact };
      return stock;
    }, {});
};

const toStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const warnAboutCoverage = ({ catalogue, cartAvailability }: ProductStockPayload) => {
  const truncatedCollections = toStringList(catalogue?.truncatedCollections);
  if (truncatedCollections.length) {
    console.warn(`Product stock covers only the first page of: ${truncatedCollections.join(', ')}`);
  }
  if (cartAvailability?.isComplete === false) {
    console.warn('Product stock could not check every cart line for unavailable products');
  }
};

export const readProductStock = (root: ParentNode): ProductStockData | null => {
  const stockJson = root.querySelector(PRODUCT_STOCK_SELECTOR)?.textContent;
  if (!stockJson) return null;
  try {
    const payload: ProductStockPayload = JSON.parse(stockJson);
    warnAboutCoverage(payload);
    return {
      variantStock: normaliseVariants(payload.catalogue?.variants),
      unavailableLines: toStringList(payload.cartAvailability?.unavailableLines),
    };
  } catch (error) {
    console.error('Failed to read product stock', error);
    return null;
  }
};

export const fetchProductStock = async (): Promise<ProductStockData | null> => {
  try {
    const root = window.Shopify?.routes?.root ?? '/';
    const response = await fetch(
      `${root}?sections=${PRODUCT_STOCK_SECTION}&stock_refresh=${Date.now()}`,
      { signal: AbortSignal.timeout(PRODUCT_STOCK_REQUEST_TIMEOUT) },
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch product stock: ${response.statusText}`);
    }
    const sections: Record<string, string | null> = await response.json();
    const sectionHtml = sections[PRODUCT_STOCK_SECTION];
    if (!sectionHtml) return null;
    return readProductStock(new DOMParser().parseFromString(sectionHtml, 'text/html'));
  } catch (error) {
    console.error('Failed to refresh product stock', error);
    return null;
  }
};
